import { useState } from 'react';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Card,
  Empty,
  ErrorState,
  Loading,
  Pill,
  StatTile,
} from '../components/ui';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/useApi';
import { money, moneyCompact, number, rate } from '../lib/format';
import type { Merchant, Paged } from '../lib/types';

export function Merchants() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const path = `/merchants?limit=50${query ? `&search=${encodeURIComponent(query)}` : ''}`;
  const { data, error, loading, reload } = useApi<Paged<Merchant>>(path);

  const active = data?.items.filter((m) => m.status === 'active').length ?? 0;
  const boarding = data?.items.filter((m) => m.status === 'boarding').length ?? 0;
  const volume = data?.items.reduce((s, m) => s + m.monthlyVolumeCents, 0) ?? 0;

  return (
    <>
      <PageHeader breadcrumb={['Portfolio', 'Merchants']} title="Merchants" />

      <ScopeBar>
        Every boarded merchant beneath <strong>{user?.nodeName}</strong>. An agent sees
        only the merchants they wrote.
      </ScopeBar>

      <PageBody>
        <div className="tilerow">
          <StatTile label="In this view" value={number(data?.total ?? 0)} />
          <StatTile label="Active" value={number(active)} />
          <StatTile
            label="Boarding"
            value={number(boarding)}
            footnote="MID issued, hardware shipping"
          />
          <StatTile
            label="Monthly volume"
            value={moneyCompact(volume)}
            footnote="Across the merchants listed"
          />
        </div>

        <Card>
          <div className="toolbar">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setQuery(search);
              }}
            >
              <input
                className="input"
                type="search"
                placeholder="Search by name or MID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 280 }}
              />
            </form>
          </div>

          {loading && <Loading />}
          {error && <ErrorState error={error} onRetry={reload} />}

          {data && data.items.length === 0 && (
            <Empty
              title="No merchants match"
              detail={
                query
                  ? `Nothing matches “${query}” in your scope.`
                  : 'Nothing has boarded beneath you yet.'
              }
            />
          )}

          {data && data.items.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Merchant</th>
                    <th>Agent</th>
                    <th>Status</th>
                    <th className="num">Monthly volume</th>
                    <th className="num">Eff. rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <div className="cell-primary">{m.dba}</div>
                        <div className="cell-sub">
                          MID {m.mid} · MCC {m.mcc}
                        </div>
                      </td>
                      <td>{m.agentName}</td>
                      <td>
                        <Pill
                          tone={
                            m.status === 'active'
                              ? 'ok'
                              : m.status === 'boarding'
                                ? 'info'
                                : 'neutral'
                          }
                        >
                          {m.status}
                        </Pill>
                      </td>
                      <td className="num">{money(m.monthlyVolumeCents)}</td>
                      <td className="num">{rate(m.effectiveRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </PageBody>
    </>
  );
}
